import React, { Fragment, useContext } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { useLanguageFromUrl } from "../i18n/useLanguageFromUrl";
import { RedirectWaymenu } from "../components/RedirectWaymenu";
import { AuthContext } from "../contexts/auth";
import { MenuProvider } from "../contexts/menu";
import { NavegacaoProvider } from "../contexts/navegacao";
import { DefaultLayout } from "../layouts/DefaultLayout";
import Carregando from "../pages/carregando";
import authRequired from "./authRequired.json";
import plataformaCashbackPages from "./plataformaCashbackPages.json";
import RelatorioPdf from "../pages/analiseRelatorios/cadastrais/fichaTecnica/relatorioPdf";
import RelatorioPdfPisCofins from "../pages/analiseRelatorios/cadastrais/tributacao/piscofins/relatorioPdfPiscofins";
import RelatorioPdfContasAPagar from "../pages/analiseRelatorios/financas/contasAPagar/relatorioPdfContasAPagar";
import RelatorioPdfContasAReceber from "../pages/analiseRelatorios/financas/contasAReceber/relatorioPdfContasAReceber";
import { RecuperarSenha } from "../pages/recuperacaoSenha";
import DetalhesPlano from "../pages/gerenciamentoAssinaturaPlanos/detalhesPlano";
import Home from "../pages/home";
import PaginaLogin from "../pages/login";
import SelecaoEmpresas from "../pages/selecaoEmpresas";
import { obterStorage } from "../utils/secureStorage";
import WizardPage from "../pages/wizard/index";
import { useAuthRoute } from "./hooks/useAuthRoute";

const lazyImportedPages = authRequired.pages.map((page) => {
  const nivel = page.nivelNecessario;
  const Component = React.lazy(() => import(`/src/pages/${page.caminho}`));
  const permissoes = page.permissoes;

  return {
    ...page,
    Element: () => {
      return useAuthRoute(Component, permissoes, nivel, page.tipoEmpresa);
    },
  };
});

const lazyImportedPagesPlataforma = plataformaCashbackPages.pages.map((page) => {
  return {
    ...page,
    Element: React.lazy(() => import(`/src/pages/${page.caminho}`)),
  };
});
const ListagemCashback = React.lazy(() => import("../pages/cashback/listagem"));
const RelatorioCashBack = React.lazy(() => import("../pages/cashback/relatorios/relatorio"));
const CashBackPDF = React.lazy(() => import("../pages/cashback/relatorios/relatorio/relatorio"));
const CadastroCashBack = React.lazy(() => import("../pages/cashback/cadastro"));
const RelatorioEstoqueEmFalta = React.lazy(
  () => import("../pages/analiseRelatorios/estoque/emFalta/relatorioPdf"),
);
const RelatorioEstoqueHistoricoMovimentacao = React.lazy(
  () => import("../pages/analiseRelatorios/estoque/historicoMovimentacoes/relatorioPdf"),
);
const RelatorioItensCancelados = React.lazy(
  () => import("../pages/analiseRelatorios/faturamento/cancelamentos/itensCancelados/relatorioPdf"),
);
const EspelhoVendaPdf = React.lazy(() => import("../pages/consultasRapidas/atendimentos/pdfFicha"));

//Novas Rotas, declarar em  authRequired.json, com informações de permissões e nível necessário para acesso, caso não sejam casos especiais.

/**
 * LanguageSyncer
 *
 * Componente sem renderização visual que fica dentro do <BrowserRouter>
 * e escuta mudanças no parâmetro ?language=<lang> da URL para atualizar
 * o idioma do i18n de forma reativa.
 */
const LanguageSyncer = () => {
  useLanguageFromUrl();
  return null;
};

const RoutesApp = () => {
  const { logado, loading, pagamentoNecessario, usuarioMaster } = useContext(AuthContext);

  const sessao = obterStorage("sessao");

  const permissoesWaybe = sessao ? sessao?.contratoWaybeAtualDTO?.permissoesWaybe : null;

  const tipoPerfilUsuarioMaster = sessao
    ? sessao?.usuarioLogadoDTO?.usuarioPerfilEmpresaDTO?.tipoPerfilUsuario === "MASTER"
    : null;

  const temPrivilegioCashback = sessao
    ? sessao?.usuarioLogadoDTO?.usuarioPerfilEmpresaDTO?.listaPrivilegios?.PARAM_REL_CASHBACK
    : null;

  const temContratoCashback = permissoesWaybe?.includes("CONTROLE_CASHBACK");

  return (
    <NavegacaoProvider>
      <BrowserRouter>
        <LanguageSyncer />
        <MenuProvider>
          <Fragment>
            <Routes>
              {loading ? (
                <Route path="*" element={<Carregando />} />
              ) : !logado ? (
                <Fragment>
                  <Route path="*" element={<Navigate to="/login" />} />
                  <Route path="login" element={<PaginaLogin />} />
                  <Route path="cadastro/:idIntegrador" element={<WizardPage />} />
                  <Route path="cadastro/usuario/recuperacao" element={<RecuperarSenha />} />

                  {lazyImportedPagesPlataforma.map(({ Element, ...page }, index) => {
                    return (
                      <Route
                        key={`plataforma-${page.url}-${index}`}
                        path={page.url}
                        element={
                          <React.Suspense fallback={<Carregando />}>
                            <Element />
                          </React.Suspense>
                        }
                      />
                    );
                  })}
                </Fragment>
              ) : pagamentoNecessario ? (
                usuarioMaster ? (
                  <Fragment>
                    <Route path="*" element={<DefaultLayout />}>
                      <Route path="empresa" element={<SelecaoEmpresas />} />
                      <Route path="*" element={<Navigate to="/gerenciar-plano" />} />
                      <Route path="gerenciar-plano" element={<DetalhesPlano />} />
                    </Route>
                  </Fragment>
                ) : (
                  <Fragment>
                    <Route path="*" element={<DefaultLayout />}>
                      <Route path="*" element={<Navigate to="/home" />} />
                      <Route path="empresa" element={<SelecaoEmpresas />} />
                      <Route path="home" element={<Home />} />
                    </Route>
                  </Fragment>
                )
              ) : (
                <Fragment>
                  <Route path="login" element={<Navigate to="/" />} />
                  <Route path="/" element={<DefaultLayout />}>
                    {lazyImportedPages.map(({ Element, ...page }) => {
                      return (
                        <Route
                          key={"rota" + page.url}
                          path={page.url}
                          element={
                            <React.Suspense fallback={<Carregando />}>
                              <Element />
                            </React.Suspense>
                          }
                        />
                      );
                    })}

                    <Route
                      path="/"
                      element={
                        <React.Suspense fallback={<Carregando />}>
                          <Home />
                        </React.Suspense>
                      }
                    />

                    {lazyImportedPages.map(({ Element, ...page }) => {
                      return (
                        <Route
                          key={"rotaHeader" + page.url}
                          path={page.url}
                          element={
                            <React.Suspense fallback={<Carregando />}>
                              <Element />
                            </React.Suspense>
                          }
                        />
                      );
                    })}

                    {temContratoCashback && (temPrivilegioCashback || tipoPerfilUsuarioMaster) && (
                      <Route
                        path="relatorios/cashback"
                        element={
                          <React.Suspense fallback={<Carregando />}>
                            <RelatorioCashBack />
                          </React.Suspense>
                        }
                      />
                    )}
                  </Route>

                  <Route
                    path="relatorios/cashback/pdf"
                    element={
                      <React.Suspense fallback={<Carregando />}>
                        <CashBackPDF />
                      </React.Suspense>
                    }
                  />

                  <Route
                    path="analise-relatorios/faturamento/cancelamentos/itens-cancelados/pdf"
                    element={
                      <React.Suspense fallback={<Carregando />}>
                        <RelatorioItensCancelados />
                      </React.Suspense>
                    }
                  />

                  <Route
                    path="/consultas-rapidas/atendimentos/espelho-venda"
                    element={
                      <React.Suspense fallback={<Carregando />}>
                        <EspelhoVendaPdf />
                      </React.Suspense>
                    }
                  />

                  <Route
                    path="/analise-relatorios/estoque/em-falta/pdf"
                    element={
                      <React.Suspense fallback={<Carregando />}>
                        <RelatorioEstoqueEmFalta />
                      </React.Suspense>
                    }
                  />

                  <Route
                    path="analise-relatorios/estoque/historico-movimentacoes/pdf"
                    element={
                      <React.Suspense>
                        <RelatorioEstoqueHistoricoMovimentacao />
                      </React.Suspense>
                    }
                  />

                  <Route
                    path="fidelidade/cashback/cadastro"
                    element={
                      <React.Suspense fallback={<Carregando />}>
                        <CadastroCashBack />
                      </React.Suspense>
                    }
                  />

                  <Route
                    path="analiseRelatorios/cadastrais/fichaTecnica.pdf"
                    element={
                      <React.Suspense fallback={<Carregando />}>
                        <RelatorioPdf />
                      </React.Suspense>
                    }
                  />
                  <Route
                    path="/analise-relatorios/financas/contas-a-pagar/pdf"
                    element={
                      <React.Suspense fallback={<Carregando />}>
                        <RelatorioPdfContasAPagar />
                      </React.Suspense>
                    }
                  />
                  <Route
                    path="/analise-relatorios/financas/contas-a-receber/pdf"
                    element={
                      <React.Suspense fallback={<Carregando />}>
                        <RelatorioPdfContasAReceber />
                      </React.Suspense>
                    }
                  />
                  <Route
                    path="/analise-relatorios/cadastrais/tributacao/piscofins/pdf"
                    element={
                      <React.Suspense fallback={<Carregando />}>
                        <RelatorioPdfPisCofins />
                      </React.Suspense>
                    }
                  />

                  <Route
                    path="analise-relatorios/faturamento/cancelamentos/itens-cancelados/pdf"
                    element={
                      <React.Suspense fallback={<Carregando />}>
                        <RelatorioItensCancelados />
                      </React.Suspense>
                    }
                  />

                  <Route
                    path="/analise-relatorios/estoque/em-falta/pdf"
                    element={
                      <React.Suspense fallback={<Carregando />}>
                        <RelatorioEstoqueEmFalta />
                      </React.Suspense>
                    }
                  />

                  <Route
                    path="analise-relatorios/estoque/historico-movimentacoes/pdf"
                    element={
                      <React.Suspense>
                        <RelatorioEstoqueHistoricoMovimentacao />
                      </React.Suspense>
                    }
                  />
                  <Route
                    path="analiseRelatorios/cadastrais/fichaTecnica.pdf"
                    element={
                      <React.Suspense fallback={<Carregando />}>
                        <RelatorioPdf />
                      </React.Suspense>
                    }
                  />
                  <Route
                    path="/analise-relatorios/financas/contas-a-pagar/pdf"
                    element={
                      <React.Suspense fallback={<Carregando />}>
                        <RelatorioPdfContasAPagar />
                      </React.Suspense>
                    }
                  />
                  <Route
                    path="/analise-relatorios/financas/contas-a-receber/pdf"
                    element={
                      <React.Suspense fallback={<Carregando />}>
                        <RelatorioPdfContasAReceber />
                      </React.Suspense>
                    }
                  />
                  <Route
                    path="/analise-relatorios/cadastrais/tributacao/piscofins/pdf"
                    element={
                      <React.Suspense fallback={<Carregando />}>
                        <RelatorioPdfPisCofins />
                      </React.Suspense>
                    }
                  />

                  <Route path="waymenu/redirect/:tipo" element={<RedirectWaymenu />} />

                  {lazyImportedPages.map(({ Element, ...page }) => {
                    return (
                      <Route
                        key={"rota" + page.url}
                        path={page.url}
                        element={
                          <React.Suspense fallback={<Carregando />}>
                            <Element />
                          </React.Suspense>
                        }
                      />
                    );
                  })}
                </Fragment>
              )}
            </Routes>
          </Fragment>
        </MenuProvider>
      </BrowserRouter>
    </NavegacaoProvider>
  );
};

export default RoutesApp;
